import { Directive, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import { Permission } from 'src/app/library/config/auth/permissions/permission';
import { BooleanOperator } from 'src/app/library/enums/general/boolean-operator.enum';
import { UserAuthService } from '../../auth/services/user.service';

@Directive({
    selector: '[authorized-disabled]',
    providers: [MatTooltip]
})
export class AuthorizedDisabledDirective implements OnInit {
    @Input('authorized-disabled') permissions: Permission[] | undefined;
    // Class defining the disabled look and feel. If nothing is supplied 40% opacity overlay is applied.
    @Input('disabledClass') disabledClass: string = null;
    // Class defining the wrapper div. This can mainly be omitted but sometimes the HTML element has fixed positions which doesnt work well with the wrapper.
    @Input('disabledWrapperClass') disabledWrapperClass: string = null;
    //tooltip to show when the user has permission
    @Input('authorized-tooltip') authorizedToolTip: string = null;
    // Bolean operator to use when checking permissions
    @Input('boolean-operator') booleanOperator: BooleanOperator =
        BooleanOperator.OR;

    constructor(
        private userAuthService: UserAuthService,
        private renderer: Renderer2,
        private elementRef: ElementRef,
        private tooltip: MatTooltip,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        let hasPermission = false;
        if (this.permissions && Array.isArray(this.permissions)) {
            const permissionsToCheck = this.permissions.map(String);

            switch (this.booleanOperator) {
                case BooleanOperator.AND:
                    hasPermission =
                        this.userAuthService.hasAllPermissions(
                            permissionsToCheck
                        );
                    break;
                case BooleanOperator.OR:
                    hasPermission =
                        this.userAuthService.hasPermissions(permissionsToCheck);
                    break;
                default:
                    throw new Error(
                        'Boolean operator not valid for authorization directive.'
                    );
            }
        } else {
            //Permissions not valid for authorization directive. - conditional
            return;
        }

        if (!hasPermission) {
            if (this.disabledClass) {
                this.renderer.addClass(
                    this.elementRef.nativeElement,
                    this.disabledClass
                );
            } else {
                this.renderer.addClass(
                    this.elementRef.nativeElement,
                    'disabled-element'
                );
            }

            this.renderer.setProperty(
                this.elementRef.nativeElement,
                'disabled',
                true
            );

            this.createPermissionWrapperDiv();
        } else {
            this.renderer.removeClass(
                this.elementRef.nativeElement,
                'disabled-element'
            );

            this.renderer.setProperty(
                this.elementRef.nativeElement,
                'disabled',
                false
            );

            if (this.authorizedToolTip) {
                this.createWrapperDiv();
            }
        }
    }

    private addTooltip(renderer: Renderer2, target: any, message: string) {
        if (!renderer) throw new Error('Renderer object required');

        this.renderer.listen(target, 'mouseover', () => {
            this.tooltip.message = message;
            this.tooltip.show();
        });

        this.renderer.listen(target, 'mouseleave', () => {
            this.tooltip.hide();
        });
    }

    private createWrapperDiv() {
        const wrapper = this.renderer.createElement('div');

        const parent = this.renderer.parentNode(this.elementRef.nativeElement);
        this.renderer.insertBefore(
            parent,
            wrapper,
            this.elementRef.nativeElement
        );
        this.renderer.appendChild(wrapper, this.elementRef.nativeElement);

        this.addTooltip(this.renderer, wrapper, this.authorizedToolTip);
    }

    private createPermissionWrapperDiv() {
        const wrapper = this.renderer.createElement('div');
        this.renderer.addClass(wrapper, 'disabled-wrapper');
        if (this.disabledWrapperClass)
            this.renderer.addClass(wrapper, this.disabledWrapperClass);

        this.renderer.listen(wrapper, 'click', e => {
            e.preventDefault();
            e.stopPropagation();
            this.toastr.warning('You do not have the required permissions.');
        });

        const parent = this.renderer.parentNode(this.elementRef.nativeElement);
        this.renderer.insertBefore(
            parent,
            wrapper,
            this.elementRef.nativeElement
        );
        this.renderer.appendChild(wrapper, this.elementRef.nativeElement);

        this.addTooltip(
            this.renderer,
            wrapper,
            'You do not have the required permission.'
        );
    }
}
